import { useEffect } from "react";
import Banner from "../../sections/common/banner";
import { bannerData } from "../../../globals/banner";
import { loadScript } from "../../../globals/constants";
import {
    privacyPolicyMeta,
    privacyPolicySections,
} from "../../../globals/privacy-policy-content";
import "./privacy-policy.css";

function PolicyBlock({ block }) {
    if (block.type === "p") {
        return <p>{block.text}</p>;
    }

    if (block.type === "h4") {
        return <h4>{block.text}</h4>;
    }

    if (block.type === "ul") {
        return (
            <ul>
                {block.items.map((item) => (
                    <li key={item}>{item}</li>
                ))}
            </ul>
        );
    }

    if (block.type === "definitions") {
        return (
            <dl className="privacy-definitions">
                {block.items.map((item) => (
                    <div className="privacy-definition" key={item.term}>
                        <dt>{item.term}</dt>
                        <dd>{item.text}</dd>
                    </div>
                ))}
            </dl>
        );
    }

    return null;
}

function PrivacyPolicyPage() {
    useEffect(() => {
        loadScript("js/custom.js");
    }, []);

    return (
        <>
            <Banner _data={bannerData.privacy} />

            <div className="section-full p-t80 p-b80 privacy-policy-page">
                <div className="container privacy-inner">
                    <div className="privacy-hero-card m-b30">
                        <div className="privacy-kicker">Official company policy</div>
                        <h1 className="privacy-title">{privacyPolicyMeta.title}</h1>
                        <p className="privacy-subtitle">{privacyPolicyMeta.subtitle}</p>
                        <p className="privacy-lead">
                            This policy explains how InfoTrace Analytics Limited collects,
                            processes and protects personal data under the GDPR and the Kenya
                            Data Protection Act, 2019. It applies to staff, clients,
                            suppliers and anyone whose personal information we handle.
                        </p>
                    </div>

                    <div className="privacy-meta-grid m-b30">
                        <div className="privacy-meta-card">
                            <h3>Document version</h3>
                            <dl className="privacy-meta-list">
                                <div>
                                    <dt>Version number</dt>
                                    <dd>{privacyPolicyMeta.version}</dd>
                                </div>
                                <div>
                                    <dt>First version date</dt>
                                    <dd>{privacyPolicyMeta.firstVersionDate}</dd>
                                </div>
                                <div>
                                    <dt>Policy date</dt>
                                    <dd>{privacyPolicyMeta.policyDate}</dd>
                                </div>
                                <div>
                                    <dt>Designation</dt>
                                    <dd>{privacyPolicyMeta.designation}</dd>
                                </div>
                                <div>
                                    <dt>Process executor</dt>
                                    <dd>{privacyPolicyMeta.processExecutor}</dd>
                                </div>
                                <div>
                                    <dt>Process checker (Supervisor)</dt>
                                    <dd>{privacyPolicyMeta.processChecker || "—"}</dd>
                                </div>
                            </dl>
                        </div>

                        <div className="privacy-meta-card">
                            <h3>Approval and sign-off</h3>
                            <div className="privacy-table-wrap">
                                <table className="privacy-table">
                                    <thead>
                                        <tr>
                                            <th>Role</th>
                                            <th>Name</th>
                                            <th>Signature</th>
                                            <th>Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {privacyPolicyMeta.approvals.map((row) => (
                                            <tr key={row.role}>
                                                <td>{row.role}</td>
                                                <td>{row.name}</td>
                                                <td>
                                                    {row.signature || (
                                                        <span className="privacy-signature-line" />
                                                    )}
                                                </td>
                                                <td>{row.date || "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    <div className="privacy-layout">
                        <aside className="privacy-toc">
                            <h3>Contents</h3>
                            <ul>
                                {privacyPolicySections.map((section) => (
                                    <li key={section.id}>
                                        <a href={`#${section.id}`}>
                                            {section.number}. {section.title}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </aside>

                        <div className="privacy-content">
                            {privacyPolicySections.map((section) => (
                                <section
                                    className="privacy-section"
                                    id={section.id}
                                    key={section.id}
                                >
                                    <h2>
                                        {section.number}. {section.title}
                                    </h2>
                                    {section.blocks.map((block, index) => (
                                        <PolicyBlock
                                            key={`${section.id}-${block.type}-${index}`}
                                            block={block}
                                        />
                                    ))}
                                </section>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default PrivacyPolicyPage;